import { query } from "./client.js";
import { supabase, useSupabase } from "./supabaseClient.js";

/**
 * List all sales with customer and payment information
 * @returns {Promise<Array>} Array of sales records
 */
export async function listSales() {
  try {
    if (useSupabase()) {
      // First, get sales without images
      const { data: salesData, error: salesError } = await supabase
        .from("sales")
        .select(`
          id_sales,
          doc_number,
          payment_method,
          payment_status,
          payment_intent_id,
          payment_charge_id,
          refund_id,
          refund_amount,
          is_canceled,
          notes,
          subtotal,
          discount_total,
          tax_total,
          total,
          created_at,
          customers (
            id_customers,
            name
          )
        `)
        .order("created_at", { ascending: false });
      
      if (salesError) throw salesError;
      
      // Then get first product image for each sale
      const salesWithImages = await Promise.all((salesData || []).map(async (s) => {
        let productImage = null;
        let productImageMime = 'image/jpeg';
        let productName = null;
        
        try {
          // Get first sale item
          const { data: items } = await supabase
            .from("sale_items")
            .select("product_id, products(name)")
            .eq("sale_id", s.id_sales)
            .limit(1);
          
          if (items?.[0]) {
            productName = items[0]?.products?.name || null;
          }

          if (items?.[0]?.product_id) {
            // Get first product image
            const { data: images } = await supabase
              .from("product_images")
              .select("image_base64, mime")
              .eq("product_id", items[0].product_id)
              .limit(1);
            
            if (images?.[0]) {
              productImage = images[0].image_base64;
              productImageMime = images[0].mime || 'image/jpeg';
            }
          }
        } catch (err) {
          console.warn(`Failed to load image for sale ${s.id_sales}:`, err);
        }
        
        return {
          id: s.id_sales,
          doc_number: s.doc_number,
          payment_method: s.payment_method,
          payment_status: s.payment_status || null,
          payment_intent_id: s.payment_intent_id || null,
          payment_charge_id: s.payment_charge_id || null,
          refund_id: s.refund_id || null,
          refund_amount: Number(s.refund_amount || 0),
          is_canceled: Boolean(s.is_canceled || 0),
          notes: s.notes,
          subtotal: Number(s.subtotal || 0),
          discount_total: Number(s.discount_total || 0),
          tax_total: Number(s.tax_total || 0),
          total: Number(s.total || 0),
          created_at: s.created_at,
          customer_id: s.customers?.id_customers || null,
          customer_name: s.customers?.name || "Sin cliente",
          product_image: productImage,
          product_image_mime: productImageMime,
          product_name: productName,
        };
      }));
      
      return salesWithImages;
    }

    // SQLite - try with images, fallback without
    let rows;
    try {
      rows = await query(`
        SELECT 
          s.id_sales,
          s.doc_number,
          s.payment_method,
          s.notes,
          s.subtotal,
          s.discount_total,
          s.tax_total,
          s.total,
          s.created_at,
          s.customer_id,
          c.name AS customer_name,
          (
            SELECT pi.image_base64 
            FROM sale_items si2
            LEFT JOIN products p2 ON p2.id_products = si2.product_id
            LEFT JOIN product_images pi ON pi.product_id = p2.id_products
            WHERE si2.sale_id = s.id_sales
            LIMIT 1
          ) AS image_base64,
          (
            SELECT pi.mime 
            FROM sale_items si2
            LEFT JOIN products p2 ON p2.id_products = si2.product_id
            LEFT JOIN product_images pi ON pi.product_id = p2.id_products
            WHERE si2.sale_id = s.id_sales
            LIMIT 1
          ) AS mime,
          (
            SELECT p2.name
            FROM sale_items si2
            LEFT JOIN products p2 ON p2.id_products = si2.product_id
            WHERE si2.sale_id = s.id_sales
            LIMIT 1
          ) AS first_product_name
        FROM sales s
        LEFT JOIN customers c ON c.id_customers = s.customer_id
        ORDER BY s.created_at DESC
      `);
    } catch (err) {
      console.warn('Failed to load with images, trying without:', err);
      // Fallback without images
      rows = await query(`
        SELECT 
          s.id_sales,
          s.doc_number,
          s.payment_method,
          s.notes,
          s.subtotal,
          s.discount_total,
          s.tax_total,
          s.total,
          s.created_at,
          s.customer_id,
          c.name AS customer_name
        FROM sales s
        LEFT JOIN customers c ON c.id_customers = s.customer_id
        ORDER BY s.created_at DESC
      `);
    }

    return rows.map(r => ({
      id: r.id_sales,
      doc_number: r.doc_number,
      payment_method: r.payment_method,
      notes: r.notes,
      subtotal: Number(r.subtotal || 0),
      discount_total: Number(r.discount_total || 0),
      tax_total: Number(r.tax_total || 0),
      total: Number(r.total || 0),
      created_at: r.created_at,
      customer_id: r.customer_id,
      customer_name: r.customer_name || "Sin cliente",
      product_image: r.image_base64 || null,
      product_image_mime: r.mime || 'image/jpeg',
      product_name: r.first_product_name || null,
    }));
  } catch (err) {
    console.error("Error listing sales:", err);
    return [];
  }
}

/**
 * Get detailed sale information by ID
 * @param {number} saleId - Sale ID
 * @returns {Promise<Object|null>} Sale details or null
 */
export async function getSaleById(saleId) {
  try {
    if (useSupabase()) {
      const { data, error } = await supabase
        .from("sales")
        .select(`
          id_sales,
          doc_number,
          payment_method,
          notes,
          subtotal,
          discount_total,
          tax_total,
          total,
          created_at,
          customers (
            id_customers,
            name,
            phone,
            email,
            address
          )
        `)
        .eq("id_sales", saleId)
        .single();
      
      if (error) throw error;
      if (!data) return null;

      return {
        id: data.id_sales,
        doc_number: data.doc_number,
        payment_method: data.payment_method,
        notes: data.notes,
        subtotal: Number(data.subtotal || 0),
        discount_total: Number(data.discount_total || 0),
        tax_total: Number(data.tax_total || 0),
        total: Number(data.total || 0),
        created_at: data.created_at,
        customer: data.customers ? {
          id: data.customers.id_customers,
          name: data.customers.name,
          phone: data.customers.phone,
          email: data.customers.email,
          address: data.customers.address,
        } : null,
      };
    }

    // SQLite
    const rows = await query(
      `
      SELECT 
        s.id_sales,
        s.doc_number,
        s.payment_method,
        s.notes,
        s.subtotal,
        s.discount_total,
        s.tax_total,
        s.total,
        s.created_at,
        c.id_customers,
        c.name AS customer_name,
        c.phone AS customer_phone,
        c.email AS customer_email,
        c.address AS customer_address
      FROM sales s
      LEFT JOIN customers c ON c.id_customers = s.customer_id
      WHERE s.id_sales = ?
    `,
      [saleId]
    );

    if (!rows.length) return null;
    const r = rows[0];

    return {
      id: r.id_sales,
      doc_number: r.doc_number,
      payment_method: r.payment_method,
      notes: r.notes,
      subtotal: Number(r.subtotal || 0),
      discount_total: Number(r.discount_total || 0),
      tax_total: Number(r.tax_total || 0),
      total: Number(r.total || 0),
      created_at: r.created_at,
      customer: r.id_customers ? {
        id: r.id_customers,
        name: r.customer_name,
        phone: r.customer_phone,
        email: r.customer_email,
        address: r.customer_address,
      } : null,
    };
  } catch (err) {
    console.error("Error getting sale by ID:", err);
    return null;
  }
}

/**
 * Get all items for a specific sale
 * @param {number} saleId - Sale ID
 * @returns {Promise<Array>} Array of sale items
 */
export async function getSaleItems(saleId) {
  try {
    if (useSupabase()) {
      const { data, error } = await supabase
        .from("sale_items")
        .select(`
          id_sale_items,
          quantity,
          unit_price,
          discount,
          tax_rate,
          total,
          products (
            id_products,
            name,
            sku,
            discount,
            tax_id,
            product_images (
              image_base64,
              mime
            )
          )
        `)
        .eq("sale_id", saleId);
      
      if (error) throw error;
      
      return (data || []).map(item => ({
        id: item.id_sale_items,
        product_id: item.products?.id_products || null,
        product_name: item.products?.name || "Producto eliminado",
        product_sku: item.products?.sku || "",
        product_image: item.products?.product_images?.[0]?.image_base64 || null,
        product_image_mime: item.products?.product_images?.[0]?.mime || 'image/jpeg',
        quantity: Number(item.quantity || 0),
        unit_price: Number(item.unit_price || 0),
        discount: Number(item.discount || 0),
        tax_rate: Number(item.tax_rate || 0),
        total: Number(item.total || 0),
        product_discount_percent: Number(item.products?.discount || 0),
        product_tax_id: item.products?.tax_id || null,
      }));
    }

    // SQLite
    const rows = await query(
      `
      SELECT 
        si.id_sale_items,
        si.product_id,
        si.quantity,
        si.unit_price,
        si.discount,
        si.tax_rate,
        si.total,
        p.name AS product_name,
        p.sku AS product_sku,
        p.discount AS product_discount_percent,
        t.rate AS product_tax_rate,
        pi.image_base64,
        pi.mime
      FROM sale_items si
      LEFT JOIN products p ON p.id_products = si.product_id
      LEFT JOIN taxes t ON t.id_taxes = p.tax_id
      LEFT JOIN product_images pi ON pi.product_id = p.id_products
      WHERE si.sale_id = ?
      GROUP BY si.id_sale_items
      ORDER BY si.id_sale_items
    `,
      [saleId]
    );

    return rows.map(r => ({
      id: r.id_sale_items,
      product_id: r.product_id,
      product_name: r.product_name || "Producto eliminado",
      product_sku: r.product_sku || "",
      product_image: r.image_base64 || null,
      product_image_mime: r.mime || 'image/jpeg',
      quantity: Number(r.quantity || 0),
      unit_price: Number(r.unit_price || 0),
      discount: Number(r.discount || 0),
      tax_rate: Number(r.tax_rate || 0),
      total: Number(r.total || 0),
      product_discount_percent: Number(r.product_discount_percent || 0),
      product_tax_rate: Number(r.product_tax_rate || 0),
    }));
  } catch (err) {
    console.error("Error getting sale items:", err);
    return [];
  }
}

/**
 * Cancel a sale (deletes the sale and cascades its items)
 * Allowed when business rules permit; UI should enforce time window
 * @param {number} saleId
 * @returns {Promise<boolean>} true if deleted
 */
export async function cancelSale(saleId, opts = {}) {
  try {
    if (useSupabase()) {
      const payload = {
        is_canceled: 1,
        canceled_at: new Date().toISOString(),
      };
      if (opts?.reason) payload.canceled_reason = String(opts.reason);
      if (opts?.payment_status) payload.payment_status = String(opts.payment_status);
      if (opts?.refund_id) payload.refund_id = String(opts.refund_id);
      if (opts?.refund_amount) payload.refund_amount = Number(opts.refund_amount || 0);
      const { error } = await supabase
        .from('sales')
        .update(payload)
        .eq('id_sales', saleId);
      if (error) throw error;
      return true;
    }
    await query('DELETE FROM sales WHERE id_sales = ?', [saleId]);
    return true;
  } catch (err) {
    console.error('Error canceling sale:', err);
    return false;
  }
}
